import React, { useState } from 'react';
import { Button } from '../shared/Button';
import { Card } from '../shared/Card';
interface PlanCreationFlowProps {
  onBack: () => void;
}
export const PlanCreationFlow = ({
  onBack
}: PlanCreationFlowProps) => {
  const [step, setStep] = useState(0);
  const [planData, setPlanData] = useState({
    goal: '',
    frequency: '',
    duration: '',
    focusAreas: []
  });
  const updatePlanData = (field, value) => {
    setPlanData({
      ...planData,
      [field]: value
    });
  };
  const toggleFocusArea = area => {
    if (planData.focusAreas.includes(area)) {
      updatePlanData('focusAreas', planData.focusAreas.filter(a => a !== area));
    } else {
      updatePlanData('focusAreas', [...planData.focusAreas, area]);
    }
  };
  const nextStep = () => {
    setStep(step + 1);
  };
  const prevStep = () => {
    if (step > 0) {
      setStep(step - 1);
    } else {
      onBack();
    }
  };
  const generatePlan = () => {
    // Here you would typically send the plan data to an API
    // For now, we'll just go back to the plan tracking screen
    onBack();
  };
  const renderStep = () => {
    switch (step) {
      case 0:
        return <div className="p-6 flex flex-col h-full" data-id="element-908">
            <div className="mb-6" data-id="element-909">
              <button onClick={prevStep} className="text-gray-500 mb-4" data-id="element-910">
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-911">
                What's your main fitness goal?
              </h1>
              <p className="text-gray-600 mb-6" data-id="element-912">
                This helps us create the right plan for you.
              </p>
            </div>
            <div className="space-y-4 mb-8" data-id="element-913">
              {[{
              value: 'lose-weight',
              label: 'Lose Weight',
              icon: '🔥',
              description: 'Burn calories and reduce body fat'
            }, {
              value: 'build-muscle',
              label: 'Build Muscle',
              icon: '💪',
              description: 'Increase strength and muscle mass'
            }, {
              value: 'improve-fitness',
              label: 'Improve Fitness',
              icon: '❤️',
              description: 'Boost endurance and overall health'
            }, {
              value: 'stress-relief',
              label: 'Stress Relief',
              icon: '🧘',
              description: 'Reduce stress and improve wellbeing'
            }].map(goal => <Card key={goal.value} onClick={() => updatePlanData('goal', goal.value)} active={planData.goal === goal.value} className="flex items-center p-4" data-id="element-914">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4 text-xl" data-id="element-915">
                    {goal.icon}
                  </div>
                  <div data-id="element-916">
                    <h3 className="font-medium text-gray-900" data-id="element-917">{goal.label}</h3>
                    <p className="text-sm text-gray-500" data-id="element-918">{goal.description}</p>
                  </div>
                </Card>)}
            </div>
            <div className="mt-auto" data-id="element-919">
              <Button onClick={nextStep} variant="primary" fullWidth disabled={!planData.goal} data-id="element-920">
                Continue
              </Button>
            </div>
          </div>;
      case 1:
        return <div className="p-6 flex flex-col h-full" data-id="element-921">
            <div className="mb-6" data-id="element-922">
              <button onClick={prevStep} className="text-gray-500 mb-4" data-id="element-923">
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-924">
                How often can you work out?
              </h1>
              <p className="text-gray-600 mb-6" data-id="element-925">
                Choose a realistic frequency you can maintain.
              </p>
            </div>
            <div className="space-y-4 mb-8" data-id="element-926">
              {[{
              value: '2-3',
              label: '2-3 days per week',
              description: 'Good for beginners'
            }, {
              value: '3-4',
              label: '3-4 days per week',
              description: 'Balanced commitment'
            }, {
              value: '4-5',
              label: '4-5 days per week',
              description: 'Dedicated routine'
            }, {
              value: '6+',
              label: '6+ days per week',
              description: 'Advanced commitment'
            }].map(frequency => <Card key={frequency.value} onClick={() => updatePlanData('frequency', frequency.value)} active={planData.frequency === frequency.value} className="flex items-center p-4" data-id="element-927">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4 text-blue-500 font-bold" data-id="element-928">
                    {frequency.value}
                  </div>
                  <div data-id="element-929">
                    <h3 className="font-medium text-gray-900" data-id="element-930">
                      {frequency.label}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-931">
                      {frequency.description}
                    </p>
                  </div>
                </Card>)}
            </div>
            <div className="mt-auto" data-id="element-932">
              <Button onClick={nextStep} variant="primary" fullWidth disabled={!planData.frequency} data-id="element-933">
                Continue
              </Button>
            </div>
          </div>;
      case 2:
        return <div className="p-6 flex flex-col h-full" data-id="element-934">
            <div className="mb-6" data-id="element-935">
              <button onClick={prevStep} className="text-gray-500 mb-4" data-id="element-936">
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-937">
                How long for each workout?
              </h1>
              <p className="text-gray-600 mb-6" data-id="element-938">
                Select your preferred workout duration.
              </p>
            </div>
            <div className="space-y-4 mb-8" data-id="element-939">
              {[{
              value: '15-20',
              label: '15-20 minutes',
              description: 'Quick and effective'
            }, {
              value: '30-40',
              label: '30-40 minutes',
              description: 'Standard workout length'
            }, {
              value: '45-60',
              label: '45-60 minutes',
              description: 'Comprehensive sessions'
            }, {
              value: '60+',
              label: '60+ minutes',
              description: 'Extended training'
            }].map(duration => <Card key={duration.value} onClick={() => updatePlanData('duration', duration.value)} active={planData.duration === duration.value} className="flex items-center p-4" data-id="element-940">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4 text-blue-500" data-id="element-941">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-942">
                      <circle cx="12" cy="12" r="10" data-id="element-943"></circle>
                      <polyline points="12 6 12 12 16 14" data-id="element-944"></polyline>
                    </svg>
                  </div>
                  <div data-id="element-945">
                    <h3 className="font-medium text-gray-900" data-id="element-946">
                      {duration.label}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-947">
                      {duration.description}
                    </p>
                  </div>
                </Card>)}
            </div>
            <div className="mt-auto" data-id="element-948">
              <Button onClick={nextStep} variant="primary" fullWidth disabled={!planData.duration} data-id="element-949">
                Continue
              </Button>
            </div>
          </div>;
      case 3:
        return <div className="p-6 flex flex-col h-full" data-id="element-950">
            <div className="mb-6" data-id="element-951">
              <button onClick={prevStep} className="text-gray-500 mb-4" data-id="element-952">
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-953">
                What areas would you like to focus on?
              </h1>
              <p className="text-gray-600 mb-6" data-id="element-954">
                Select all that apply. You can choose multiple.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-955">
              {[{
              value: 'upper-body',
              label: 'Upper Body',
              icon: '💪'
            }, {
              value: 'lower-body',
              label: 'Lower Body',
              icon: '🦵'
            }, {
              value: 'core',
              label: 'Core',
              icon: '⚡'
            }, {
              value: 'cardio',
              label: 'Cardio',
              icon: '❤️'
            }, {
              value: 'flexibility',
              label: 'Flexibility',
              icon: '🧘'
            }, {
              value: 'balance',
              label: 'Balance',
              icon: '🧠'
            }].map(area => <Card key={area.value} onClick={() => toggleFocusArea(area.value)} active={planData.focusAreas.includes(area.value)} className="p-4 flex flex-col items-center text-center" data-id="element-956">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2 text-2xl" data-id="element-957">
                    {area.icon}
                  </div>
                  <h3 className="font-medium" data-id="element-958">{area.label}</h3>
                </Card>)}
            </div>
            <div className="mt-auto" data-id="element-959">
              <Button onClick={nextStep} variant="primary" fullWidth disabled={planData.focusAreas.length === 0} data-id="element-960">
                Continue
              </Button>
            </div>
          </div>;
      case 4:
        return <div className="p-6 flex flex-col h-full" data-id="element-961">
            <div className="mb-6" data-id="element-962">
              <button onClick={prevStep} className="text-gray-500 mb-4" data-id="element-963">
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-964">
                Your Plan is Ready!
              </h1>
              <p className="text-gray-600 mb-6" data-id="element-965">
                We've created a personalized plan based on your preferences.
              </p>
            </div>
            <div className="bg-blue-50 rounded-xl p-5 mb-6" data-id="element-966">
              <h2 className="font-semibold text-blue-800 mb-2" data-id="element-967">Plan Summary</h2>
              <ul className="mt-3 space-y-2 text-sm text-blue-700" data-id="element-968">
                <li className="flex items-start" data-id="element-969">
                  <span className="mr-2" data-id="element-970">✓</span>
                  <span data-id="element-971">
                    Goal:{' '}
                    {planData.goal === 'lose-weight' ? 'Weight Loss' : planData.goal === 'build-muscle' ? 'Muscle Building' : planData.goal === 'improve-fitness' ? 'Improved Fitness' : 'Stress Relief'}
                  </span>
                </li>
                <li className="flex items-start" data-id="element-972">
                  <span className="mr-2" data-id="element-973">✓</span>
                  <span data-id="element-974">{planData.frequency} workouts per week</span>
                </li>
                <li className="flex items-start" data-id="element-975">
                  <span className="mr-2" data-id="element-976">✓</span>
                  <span data-id="element-977">{planData.duration} minute sessions</span>
                </li>
                <li className="flex items-start" data-id="element-978">
                  <span className="mr-2" data-id="element-979">✓</span>
                  <span data-id="element-980">
                    Focus areas:{' '}
                    {planData.focusAreas.map(area => area.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())).join(', ')}
                  </span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-100 rounded-xl p-4 mb-6" data-id="element-981">
              <div className="flex items-center mb-2" data-id="element-982">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 text-gray-700" data-id="element-983">
                  <circle cx="12" cy="12" r="10" data-id="element-984"></circle>
                  <line x1="12" y1="8" x2="12" y2="12" data-id="element-985"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16" data-id="element-986"></line>
                </svg>
                <span className="font-medium text-gray-700" data-id="element-987">Plan Details</span>
              </div>
              <p className="text-sm text-gray-600" data-id="element-988">
                Your plan will include {planData.frequency.split('-')[0]} to{' '}
                {planData.frequency.split('-')[1] || planData.frequency.replace('+', '')}{' '}
                workouts per week, each lasting{' '}
                {planData.duration.split('-')[0]} to{' '}
                {planData.duration.split('-')[1] || planData.duration.replace('+', '')}{' '}
                minutes. Workouts will be scheduled with appropriate rest days
                for recovery.
              </p>
            </div>
            <div className="mt-auto" data-id="element-989">
              <Button onClick={generatePlan} variant="primary" fullWidth data-id="element-990">
                Create My Plan 🎉
              </Button>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="flex flex-col h-full min-h-screen bg-white" data-id="element-991">
      {renderStep()}
    </div>;
};